import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import UI from '../../ui';
import Utils from '../../utils';
import ShipFromManager from '../../utils/ShipFromManager';
import { Instructions } from '../Instructions';
import { InboundStatus, ShippingStatus } from '../Status';
import { ValidationErrors } from '../Validations';
import ShippingEstimation from './ShippingEstimation.component';

const {
	WrappedDropdownWithLabel,
	WrappedCalendarWithLabel,
	WrappedButton,
	WrappedButtonType,
} = UI;
const { Validation, Util } = Utils;

const ShippingDetails = ({
	editingInbound,
	showMobileView,
	isMobile,
	saveInbound,
	updateInstructions,
	validations,
	updateValidation,
	shippingHeader,
}) => {
	const getShipFromAdresses = () => {
		const shipFromAddresses = [];

		ShipFromManager.ShipFromAddresses.forEach(address => {
			let shipFromValue = '';

			// Check for shipAddressStreet1 to determine if return address is included in shipFrom address, if not,
			// use the origin address
			if (address.shipAddressStreet1) {
				shipFromValue = `${address.shipAddressFullName} - ${address.shipAddressCity}, ${address.shipAddressState}`;

				shipFromAddresses.push({
					shipFromLabel: shipFromValue,
					name: address.shipAddressFullName,
					city: address.shipAddressCity,
					stateProvince: address.shipAddressState,
					countryCode: 'US',
					id: shipFromValue,
					companyName: address.shipAddressCompany,
					addressLine1: address.shipAddressStreet1,
					addressLine2: address.shipAddressStreet2,
					addressLine3: address.shipAddressStreet3,
					postalCode: address.originZip,
					phone: address.phone,
				});
			} else {
				shipFromValue = `${address.fullName} - ${address.city}, ${address.state}`;

				shipFromAddresses.push({
					shipFromLabel: shipFromValue,
					name: address.fullName,
					city: address.city,
					stateProvince: address.state,
					countryCode: 'US',
					id: shipFromValue,
					companyName: address.company,
					addressLine1: address.street1,
					addressLine2: address.street2,
					addressLine3: address.street3,
					postalCode: address.originZip,
					phone: address.phone,
				});
			}
		});
		return shipFromAddresses;
	};

	const [shipFromList, setShipFromList] = useState(getShipFromAdresses());
	const [selectedShipFrom, setSelectedShipFrom] = useState(null);
	const [arrivalDate, setArrivalDate] = useState(null);
	const [calendarErrorMessage, setCalendarErrorMessage] = useState(null);
	const [showEstimationTool, setShowEstimationTool] = useState(false);
	const [calendarHandlerIsSet, setCalendarHandlerIsSet] = useState(false);
	const [warehouse, setWarehouse] = useState(
		editingInbound.warehouse || {
			companyName: '',
			addressLine1: '',
			city: '',
			stateProvince: '',
			postalCode: '',
		}
	);
	const prevInbound = useRef();
	const isEditable =
		editingInbound.status !== InboundStatus.Processing &&
		editingInbound.status !== InboundStatus.Completed &&
		editingInbound.status !== InboundStatus.Cancelled;

	const validateCalendar = (dateToCheck, shipMethod, isBlur) => {
		const formattedCalendar = Util.formatCalendar(dateToCheck);
		const validDate = Validation.isValidDate(formattedCalendar);
		const updatedValidations = validations;

		if (shipMethod === ShippingStatus.Zorbit) {
			updatedValidations.arrival = 1;
		} else if (
			validDate &&
			moment(formattedCalendar, 'MM/DD/YYYY').diff(moment(), 'days') >= 0
		) {
			updatedValidations.arrival = 1;
		} else if (!validDate) {
			setCalendarErrorMessage(ValidationErrors.estimatedArrivalRequired);
			updatedValidations.arrival =
				isBlur || updatedValidations.arrival === -1 ? -1 : 0;
		} else if (validDate) {
			setCalendarErrorMessage(ValidationErrors.estimatedArrivalPast);
			updatedValidations.arrival = -1;
		}

		updateValidation(updatedValidations);
	};

	const validateShipFrom = (shipFrom, shipMethod, isBlur) => {
		const updatedValidations = validations;

		if (shipMethod === ShippingStatus.Self) {
			updatedValidations.shipFrom = 1;
		} else if (!shipFrom || isEmpty(shipFrom)) {
			updatedValidations.shipFrom =
				isBlur || validations.shipFrom === -1 ? -1 : 0;
		} else {
			updatedValidations.shipFrom = 1;
		}

		updateValidation(updatedValidations);
	};

	useEffect(() => {
		let shipFromValue = null;
		if (editingInbound.inboundId !== prevInbound.current) {
			setShowEstimationTool(false);
		}

		if (!isEmpty(editingInbound.fromAddress)) {
			const { name, city, stateProvince } = editingInbound.fromAddress;

			shipFromValue =
				!isEmpty(name) && !isEmpty(city) && !isEmpty(stateProvince)
					? `${name} - ${city}, ${stateProvince}`
					: null;
		}

		setSelectedShipFrom(shipFromValue);

		setArrivalDate(new Date(editingInbound.expectedArrivalDate));

		if (editingInbound && editingInbound.warehouse) {
			setWarehouse({
				companyName: editingInbound.warehouse.companyName,
				addressLine1: editingInbound.warehouse.addressLine1,
				city: editingInbound.warehouse.city,
				stateProvince: editingInbound.warehouse.stateProvince,
				postalCode: editingInbound.warehouse.postalCode,
			});
		} else {
			setWarehouse({
				companyName: '',
				addressLine1: '',
				city: '',
				stateProvince: '',
				postalCode: '',
			});
		}

		validateCalendar(
			new Date(editingInbound.expectedArrivalDate),
			editingInbound.shippingMethod,
			false
		);
		validateShipFrom(
			editingInbound.fromAddress,
			editingInbound.shippingMethod,
			false
		);
		prevInbound.current = editingInbound.inboundId;

		// Handling the cases where Tracking and Estimation tool is hidden when shipping status changes,
		// UI should show deafult shipping conditional text in these cases
		if (
			(editingInbound.shippingMethod === ShippingStatus.Zorbit &&
				shippingHeader === Instructions.headers.shipping.tracking) ||
			((editingInbound.shippingMethod === ShippingStatus.Self ||
				!showEstimationTool) &&
				(shippingHeader === Instructions.headers.shipping.weight ||
					shippingHeader === Instructions.headers.shipping.dimensions))
		) {
			updateInstructions(
				'shipping',
				Instructions.headers.default.shipping,
				Instructions.instructions.default.shipping
			);
		}

		// Set click handler for primereact calendar (calendar icon) click
		// Prime React 5.0 does not provide an event method to hook into the display of the calendar
		const calendarContainer = document.getElementById('arrivalDate');

		if (!calendarHandlerIsSet && calendarContainer) {
			const calendarInput = calendarContainer.querySelector(
				'input[name="expectedArrivalDate"]'
			);

			calendarContainer
				.querySelector('button.p-datepicker-trigger')
				.addEventListener('click', () => calendarInput.focus());

			// Set calendar handler to true in order to not create duplicate listeners
			setCalendarHandlerIsSet(true);
		}
	}, [editingInbound, showEstimationTool]);

	const onShipFromBlur = () => {
		validateShipFrom(
			editingInbound.fromAddress,
			editingInbound.shippingMethod,
			true
		);
	};

	const onShipFromChange = e => {
		const { value } = e.target;
		const updatedValidations = validations;
		const shipFromIndex = shipFromList.findIndex(
			shipFrom => shipFrom.shipFromLabel === value
		);
		const shipFromSelection = shipFromList[shipFromIndex];
		const updatedAddress = editingInbound.fromAddress || {};

		updatedAddress.name = shipFromSelection.name;
		updatedAddress.city = shipFromSelection.city;
		updatedAddress.stateProvince = shipFromSelection.stateProvince;
		updatedAddress.companyName = shipFromSelection.companyName;
		updatedAddress.addressLine1 = shipFromSelection.addressLine1;
		updatedAddress.addressLine2 = shipFromSelection.addressLine2;
		updatedAddress.addressLine3 = shipFromSelection.addressLine3;
		updatedAddress.postalCode = shipFromSelection.postalCode;
		updatedAddress.countryCode = shipFromSelection.countryCode;
		updatedAddress.phone = shipFromSelection.phone;
		updatedAddress.email = shipFromSelection.email;
		updatedAddress.residentialIndicator =
			shipFromSelection.residentialIndicator;

		updatedValidations.shipFrom = 1;

		setSelectedShipFrom(value);
		saveInbound({ fromAddress: updatedAddress });
		updateValidation(updatedValidations);
	};

	const onShipFromFocus = () => {
		updateInstructions(
			'shipping',
			Instructions.headers.shipping.shipFrom,
			Instructions.instructions.shipping.shipFrom
		);
	};

	const onArrivalBlur = e => {
		const { name, value } = e.target;
		const updatedDate = /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value) ? value : null;

		setArrivalDate(new Date(updatedDate));
		validateCalendar(updatedDate, editingInbound.shippingMethod, true);
		saveInbound({ [name]: moment(updatedDate, 'MM/DD/YYYY').format() });
	};

	const showArrivalInstructions = () =>
		updateInstructions(
			'shipping',
			Instructions.headers.shipping.arrival,
			Instructions.instructions.shipping.shippingArrival
		);

	const dateChange = e => {
		const { name, value } = e.target;
		const formattedDate = moment(value).format('MM/DD/YYYY');

		if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(formattedDate)) {
			setArrivalDate(value);
			validateCalendar(value, editingInbound.shippingMethod, true);
			saveInbound({ [name]: moment(value).format() });
		}
	};

	const showEstimationToolHandler = () => {
		setShowEstimationTool(prevValue => !prevValue);
	};

	const detailsClasses = showMobileView ? '' : 'p-lg-6 p-md-6 details';

	const shipTo = (
		<div className="read-only-label-container">
			<div className="read-only-label">Ship To</div>
			<div className="read-only-text">
				<p>{warehouse.companyName}</p>
				<p>{warehouse.addressLine1}</p>
				<p>{`${warehouse.city}, ${warehouse.stateProvince} ${warehouse.postalCode}`}</p>
			</div>
		</div>
	);

	const editableShippingDetails = (
		<div className={detailsClasses}>
			{editingInbound.shippingMethod === ShippingStatus.Self && (
				<>
					<WrappedCalendarWithLabel
						label="Estimated Arrival"
						isInputInvalid={validations.arrival === -1}
						showErrorBelow={isMobile}
						errorMessage={calendarErrorMessage}
						uiElementConfig={{
							value: arrivalDate,
							name: 'expectedArrivalDate',
							minDate: new Date(moment()),
							placeholder: 'MM/DD/YYYY',
							onFocus: showArrivalInstructions,
							onChange: dateChange,
							onBlur: onArrivalBlur,
							id: 'arrivalDate',
							maxLength: 10,
							dataqa: 'inbound-shipping-details-arrival-date',
						}}
					/>
					{showMobileView && editingInbound.warehouseId !== '' && (
						<div className={detailsClasses}> {shipTo} </div>
					)}
				</>
			)}
			<WrappedDropdownWithLabel
				label="Ship From"
				isInputInvalid={validations.shipFrom === -1}
				showErrorBelow={isMobile}
				errorMessage={ValidationErrors.shipFromRequired}
				uiElementConfig={{
					name: 'shipfrom',
					value: selectedShipFrom,
					placeholder: 'Select an Address',
					onChange: onShipFromChange,
					onFocus: onShipFromFocus,
					onBlur: onShipFromBlur,
					options: shipFromList,
					optionLabel: 'shipFromLabel',
					optionValue: 'id',
					dataqa: 'inbound-shipping-details-ship-from',
				}}
			/>
		</div>
	);

	const nonEditableShippingDetails = (
		<div className={detailsClasses}>
			{editingInbound.shippingMethod === ShippingStatus.Self && (
				<>
					<div className="read-only-label-container">
						<div className="read-only-label">Estimated Arrival</div>
						<div className="read-only-text">
							{arrivalDate ? moment(arrivalDate).format('MM/DD/YYYY') : ''}
						</div>
					</div>
				</>
			)}
			<div className="read-only-label-container">
				<div className="read-only-label">Ship From</div>
				<div className="read-only-text">{selectedShipFrom}</div>
			</div>
		</div>
	);

	return (
		<div className="common-inbound-details-shipping-details">
			<div className="shipping-details-header">Shipping Details</div>
			<div className="p-grid p-col-12 shipping-content">
				{isEditable && editableShippingDetails}
				{!isEditable && nonEditableShippingDetails}

				{!showMobileView &&
				editingInbound.shippingMethod === ShippingStatus.Self &&
				editingInbound.warehouseId !== '' ? (
					<div className={detailsClasses}> {shipTo}</div>
				) : (
					''
				)}
			</div>

			{/* The getRates api is not part of alpha and server work is not done yet, so confirmed with Product(Tarush) and hiding the estimation tool for now
			{editingInbound.shippingMethod === ShippingStatus.Zorbit &&
				editingInbound.status !== InboundStatus.Completed &&
				editingInbound.status !== InboundStatus.Cancelled && (
					<>
						<WrappedButton
							className="show-estimation-tool-btn"
							label={`${
								showEstimationTool ? 'Hide' : 'Show'
							} Shipping Estimation Tool`}
							icon={showEstimationTool ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
							iconPos="right"
							buttonType={WrappedButtonType.Transparent}
							dataqa="estimation-tool-button"
							onClick={showEstimationToolHandler}
						/>
						{showEstimationTool && (
							<ShippingEstimation
								isMobile={showMobileView}
								inboundId={prevInbound.current}
								updateInstructions={updateInstructions}
							/>
						)}
					</>
				)}
			*/}
		</div>
	);
};

export default ShippingDetails;
